import { useState } from 'react'
import { ExternalLink, FileText, FileSpreadsheet, File } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/utils/date'

interface DocRow {
  doc_id: string
  filename: string
  content_type: string
  deal_name: string
  uploaded_at: string
}

interface Props {
  docs: DocRow[]
  onEmpty?: React.ReactNode
}

function fileIcon(contentType: string) {
  if (contentType.includes('pdf')) return <FileText size={14} className="text-red-500" />
  if (contentType.includes('sheet') || contentType.includes('excel'))
    return <FileSpreadsheet size={14} className="text-green-600" />
  return <File size={14} className="text-gray-400" />
}

async function openDocument(doc_id: string, filename: string, contentType: string) {
  // C-08: auth is the httpOnly qoe_access_token cookie — no Authorization header to build.
  const res = await fetch(`/api/v1/documents/${doc_id}/url`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to fetch document URL')
  const data = await res.json()

  const isExcel = contentType.includes('sheet') || contentType.includes('excel')
    || filename.endsWith('.xlsx') || filename.endsWith('.xls')
  const isWord = contentType.includes('word') || filename.endsWith('.docx')

  let finalUrl = data.url

  if (isExcel || isWord) {
    finalUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(data.url)}`
  }

  window.open(finalUrl, '_blank', 'noopener,noreferrer')
}

export function DocumentList({ docs, onEmpty }: Props) {
  const [loading, setLoading] = useState<string | null>(null)

  if (!docs.length) {
    return <>{onEmpty ?? <p className="text-sm text-gray-400 py-4 text-center">No documents yet.</p>}</>
  }

  return (
    <div className="divide-y divide-gray-100">
      {docs.map(doc => (
        <div key={doc.doc_id} className="flex items-center gap-3 py-2.5 px-1">
          <span className="flex-shrink-0">{fileIcon(doc.content_type)}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{doc.filename}</p>
            <p className="text-xs text-gray-400">
              {doc.deal_name} · {formatDate(doc.uploaded_at)}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={loading === doc.doc_id}
            onClick={async () => {
              setLoading(doc.doc_id)
              try {
                await openDocument(doc.doc_id, doc.filename, doc.content_type)
              } finally {
                setLoading(null)
              }
            }}
            style={{ color: '#0d1f3c', fontSize: '12px', gap: '4px' }}
          >
            <ExternalLink size={12} />
            {loading === doc.doc_id ? 'Opening…' : 'View'}
          </Button>
        </div>
      ))}
    </div>
  )
}
